import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import ComposeAnnouncementForm from './messages/ComposeAnnouncementForm';
import SentItemsList from './messages/SentItemsList';
import LocationThreadInbox from './messages/LocationThreadInbox';
import { useLocationContext } from '../hooks/useLocationContext';
import { useListLocationThreadsQuery } from '../store/api/localApi';
import { ThreadStatus } from '../types';

// ── Messages ────────────────────────────────────────────────────────────────
//
// Scoped strictly by ACTIVE role, not by membership existence:
//   - Active PARENT → redirected to the family inbox (no Compose/Sent, even
//     if the user holds an admin role at another location/org).
//   - Active STAFF/LOCATION_ADMIN/ORG_ADMIN/SUPER_ADMIN → three-tab page
//     (Compose + Sent items + Parent messages), no Inbox. To see their own
//     inbox, an admin-who-is-also-a-parent switches their active context to
//     parent via the role switcher — same place every other parent-only view
//     lives.
// This keeps parent communications out of an admin's view by default and
// avoids cross-context leakage (CLAUDE.md: UI hides, backend enforces).
//
// Compose/Sent are the one-way ANNOUNCEMENT side and stay unchanged. "Parent
// messages" is the two-way thread inbox — a separate surface deliberately, so
// broadcasting can never be confused with replying to one family.

type Tab = 'compose' | 'sent' | 'threads';

const Messages: React.FC = () => {
  const { isParent } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<Tab>('compose');
  const { effectiveLocationId } = useLocationContext();

  // A parent message is a TASK, not a notice: someone is waiting for an answer.
  // The Messages badge can't carry that — it clears once the thread is opened,
  // and it mixes broadcast announcements in with conversations needing a reply.
  // So surface the count of threads still awaiting a staff reply (status OPEN)
  // on the tab itself, where the work actually gets picked up.
  const { data: openThreads } = useListLocationThreadsQuery(
    { locationId: effectiveLocationId ?? '', status: ThreadStatus.OPEN },
    { skip: isParent || !effectiveLocationId }
  );
  const waitingCount = openThreads?.threads.length ?? 0;

  // Push taps land on /messages?open=<deliveryId>; the param travels along.
  if (isParent) {
    return <Navigate to={{ pathname: '/parent/family/inbox', search: location.search }} replace />;
  }

  return (
    <div className="py-6">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page header */}
        <h1 className="text-xl font-bold text-gray-900 mb-6">Messages</h1>

        {/* Tab navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex gap-6" aria-label="Messages tabs">
            <button
              type="button"
              data-testid="messages-tab-compose"
              onClick={() => setActiveTab('compose')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'compose'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Compose
            </button>
            <button
              type="button"
              data-testid="messages-tab-sent"
              onClick={() => setActiveTab('sent')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'sent'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Sent items
            </button>
            <button
              type="button"
              data-testid="messages-tab-threads"
              onClick={() => setActiveTab('threads')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'threads'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Parent messages
              {waitingCount > 0 && (
                <span
                  data-testid="messages-tab-threads-waiting"
                  aria-label={`${waitingCount} awaiting a reply`}
                  className="ml-1.5 inline-flex items-center justify-center rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800"
                >
                  {waitingCount}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Tab content */}
        {activeTab === 'compose' && <ComposeAnnouncementForm />}
        {activeTab === 'sent' && <SentItemsList />}
        {activeTab === 'threads' && <LocationThreadInbox />}
      </div>
    </div>
  );
};

export default Messages;
